import {
  FormattingToolbar,
  FormattingToolbarContext,
} from "@/components/note/editor/formatting-toolbar";
import { NoteEditorMetadata } from "@/components/note/editor/note-editor-metadata";
import { FatalEditorErrorNotice } from "@/components/note/fatal-editor-error-notice";
import { MentionTooltip } from "@/components/note/mention-tooltip";
import { OutdatedClientNoteNotice } from "@/components/note/outdated-client-note-notice";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { NoteEditorMode, NoteType } from "@/components/note/types";
import { ZIndex } from "@/domains/design/constants";
import { css, cx } from "@/domains/emotion";
import { EmotionClassStyles } from "@/domains/emotion/types";
import { clientEnvModule } from "@/modules/client-env";
import { PublicAppStore, useAppStore, usePublicAppStore } from "@/store";
import { IContactModel } from "@/store/contacts/types";
import { INoteObservable, NoteContentVersion } from "@/store/note/types";
import { INotesViewPageStore } from "@/store/pages/NotesViewPageStore/types";
import { TemplateObservable } from "@/store/templates/TemplateObservable";
import styled from "@emotion/styled";
import {
  HtmlPlaceholderPreset,
  MemCommonEditor,
  MemCommonEditorAction,
  MemCommonEditorActionKind,
  MemCommonEditorInstance,
  shouldShowFormattingToolbar,
} from "@mem-labs/common-editor";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import {
  useCallback,
  useEffect,
  useRef,
  MutableRefObject,
  MouseEventHandler,
  useState,
} from "react";
import { useDebounceCallback } from "usehooks-ts";
import { NoteEditorBottomBar } from "@/components/note/editor/source-bar/note-editor-bottom-bar";
import { MDSFloatingActionMenuDropdown, useTheme } from "@/mds";
import { useNoteEditorDisplayState } from "@/components/note/note-observable-editor.hooks";
import { NoteObservable } from "@/store/note/NoteObservable";
import { useNoteVersionHistory } from "@/components/note/hooks/use-note-version-history";
import { useGhostTextForCalendarEvent } from "@/components/note/hooks/use-ghost-text-for-calendar-event";
import { EditorSpinner } from "./editor-spinner";
import { EmptyNoteActions } from "@/components/note/editor/empty-note-actions";
import { useMaybeTabContext } from "@/components/layout/tab-context";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";

type NoteObservableEditorProps = EmotionClassStyles & {
  editorStore?: MemCommonEditorStore;
  setEditorStore: (store: MemCommonEditorStore | undefined) => void;
  autoFocus?: boolean;
  autoFocusPosition?: "start" | "end";
  footer?: React.ReactNode;
  goToMention?: INotesViewPageStore["goToMention"];
  onMentionMouseOver?: INotesViewPageStore["onMentionMouseOver"];
  onMentionMouseOut?: INotesViewPageStore["onMentionMouseOut"];
  openFileUploadRejectedModal?: INotesViewPageStore["openFileUploadRejectedModal"];
  openImageUploadRejectedModal?: INotesViewPageStore["openImageUploadRejectedModal"];
  findInNote?: INotesViewPageStore["findInNote"];
  noteObservable: INoteObservable | TemplateObservable;
  myAccount?: IContactModel;
  placeholder?: string;
  showMetadata?: boolean;
  showTopPadding?: boolean;
  mode: NoteEditorMode;
  highlightText?: string;
  onReadOnlyNoteClicked?: () => void;
  noteType?: NoteType;
  versions?: NoteContentVersion[];
  onNoteRendered?: (noteId: string) => void;
  htmlPlaceholderPreset?: HtmlPlaceholderPreset;
  containerWidth?: number;
  disableGlobalListeners?: boolean;
  editorModeClassName?: string;
};

export const NoteObservableEditor = observer<NoteObservableEditorProps>(
  function NoteObservableEditor({
    editorStore,
    setEditorStore,
    containerWidth,
    autoFocus = false,
    autoFocusPosition = "start",
    className,
    footer,
    goToMention,
    openFileUploadRejectedModal,
    openImageUploadRejectedModal,
    findInNote,
    noteObservable,
    myAccount,
    placeholder,
    showMetadata = true,
    showTopPadding = true,
    mode,
    highlightText,
    onReadOnlyNoteClicked,
    noteType = NoteType.Note,
    onNoteRendered,
    htmlPlaceholderPreset,
    disableGlobalListeners = false,
    editorModeClassName,
  }) {
    const { theme, themeType } = useTheme();
    const { store, pageStore } = useAppStore();
    const maybeTab = useMaybeTabContext();
    const notesViewPage: NotesViewPageStore | undefined = maybeTab
      ? (pageStore.getPageStore(
          maybeTab.activeTab,
          maybeTab.activeTabPath,
          "notesViewPage"
        ) as NotesViewPageStore)
      : undefined;
    const { publicStore } = usePublicAppStore();

    const [editorContainer, setEditorContainer] = useState<HTMLDivElement | null>(null);
    const appendTo = useCallback(() => {
      if (!editorContainer) return document.body;

      let el: HTMLElement = editorContainer;
      while (el.parentElement && el.clientHeight > 0) {
        el = el.parentElement;
      }
      return el;
    }, [editorContainer]);

    const editorInstanceRef = useRef<MemCommonEditorInstance | null>(null);
    const dispatchAction = useCallback(
      (event: MemCommonEditorAction) => {
        if (
          mode !== NoteEditorMode.Editable &&
          event.kind !== MemCommonEditorActionKind.ApplyRemoteUpdate
        )
          return;

        return editorInstanceRef.current?.dispatchAction?.(event);
      },
      [mode]
    );

    // Sync note version history state with editor diff display
    useNoteVersionHistory(editorStore);

    // Show ghost text for empty notes with upcoming calendar events
    useGhostTextForCalendarEvent(editorStore);

    useHighlighting({
      highlightText,
      editorInstanceRef,
      mode,
    });

    const onEditorRendered = useCallback(
      (el: HTMLSpanElement | null) => {
        if (!el || !editorStore?.id) return;
        onNoteRendered?.(editorStore?.id);
      },
      [editorStore?.id, onNoteRendered]
    );

    // Preserve a ref in case state is reset before the component unmounts
    const editorRef = useRef<MemCommonEditorStore>();

    useUnmountEditorStore(editorRef, noteObservable, setEditorStore);
    useNote(editorStore, noteObservable, setEditorStore, publicStore);

    // After editorStore is nullified we can reset it
    useEffect(() => {
      if (editorStore) return;

      const newStore = new MemCommonEditorStore({
        autoFocus,
        autoFocusPosition,
        disableGlobalListeners,
        dispatchAction,
        goToMention,
        openFileUploadRejectedModal,
        openImageUploadRejectedModal,
        findInNote,
        noteObservable,
        myAccount,
        placeholder,
        publicStore,
        appStore: store,
        onReadOnlyNoteClicked,
        mode,
        theme,
        themeType,
        noteType,
        htmlPlaceholderPreset,
      });

      editorRef.current = newStore;
      setEditorStore(newStore);

      /**
       * The `MemCommonEditorStore` is tightly coupled to our ui - we need to tie
       * some of its lifecycle methods to our component lifecycle.
       *
       * This will ensure the contents of the editor remain up-to-date (E.g. when
       * receiving remote updates).
       *
       * If `mode` is set to "static", the editor will not have any lifecycle
       * methods registered.
       */

      if (mode === NoteEditorMode.Static) {
        return;
      }

      newStore.mount();

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorStore]);

    // Update autoFocusPosition when it changes (e.g., from router state)
    useEffect(() => {
      if (editorStore && editorStore.autoFocusPosition !== autoFocusPosition) {
        runInAction(() => {
          editorStore.autoFocusPosition = autoFocusPosition;
        });
      }
    }, [editorStore, autoFocusPosition]);

    useEffect(() => {
      editorStore?.setTheme({ theme, themeType });
    }, [editorStore, theme, themeType]);

    const focusEditor: React.MouseEventHandler<HTMLDivElement> = useCallback(
      e => {
        if (mode !== NoteEditorMode.Editable) return;
        e.stopPropagation();

        const editor = editorInstanceRef.current?.editor;
        if (!editor?.isEditable || editor?.isFocused) return;

        // This click can only happen above the editor (otherwise the editor would handle it instead)
        // so we focus the editor and put the cursor at the beginning of the document.
        editor.chain().focus().setTextSelection(0).run();
      },
      [mode]
    );

    const handleScroll = useDebounceCallback(
      () => {
        dispatchAction({
          kind: MemCommonEditorActionKind.ResendMentionUpdate,
          payload: null,
        });
      },
      20,
      { maxWait: 40 }
    );

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = useDebounceCallback(
      event => {
        editorStore?.setMouseY(event.clientY);
      },
      20,
      { maxWait: 40 }
    );

    const showFormattingToolbar = useCallback(
      () => shouldShowFormattingToolbar(editorInstanceRef.current?.editor),
      []
    );

    const displayState = useNoteEditorDisplayState({
      noteObservable,
      editorStore,
      mode,
      notesViewPage,
    });

    if (!editorStore) {
      return <div className={className} translate="no"></div>;
    }

    const { shouldShowBottomBar, shouldShowQuickActions } = displayState;

    const insertMenuContentClassName = css`
      max-width: calc(100vw - 20px);
      display: inline-flex;
      min-width: 176px;
      padding: ${theme.spacing[3]} 0;
      justify-content: center;
      gap: ${theme.spacing["3"]};
      width: ${editorStore?.isInsertMenuActive ? "288px" : "320px"};
      flex-direction: column;
      align-items: flex-start;
      background: ${theme.colors.surface.menu};
      border-radius: ${theme.borderRadius.large};
      border: 1px solid ${theme.colors.border.surface};
      background: ${theme.colors.surface.menu};
      box-shadow: ${theme.shadows.surface};

      & > div {
        width: 100%;
      }
    `;

    return (
      <FormattingToolbarContext.Provider value={editorStore}>
        {mode !== NoteEditorMode.Static && editorStore.editor && !!editorContainer && (
          <FormattingToolbar
            appendTo={appendTo}
            editor={editorStore.editor}
            shouldShow={showFormattingToolbar}
            editorRef={editorInstanceRef}
          />
        )}
        <EditorContainerStyles
          $mode={mode}
          className={cx(className, editorModeClassName)}
          translate="no"
          onScroll={handleScroll}
          onMouseMoveCapture={handleMouseMove}
          ref={setEditorContainer}
        >
          {showTopPadding && <TopPadding onClick={focusEditor} className={editorModeClassName} />}
          {showMetadata && (
            <NoteEditorMetadata
              className={editorModeClassName}
              store={editorStore}
              onClick={focusEditor}
            />
          )}
          {!editorStore.reloadRequired && (
            <>
              {!editorStore.hasContentError && !editorStore.hasFatalError && (
                <>
                  <MemCommonEditor
                    editorInstanceRef={editorInstanceRef}
                    editorInitializer={editorStore.editorInitializer}
                    editorEventHandler={editorStore.editorEventHandler}
                    uiTheme={editorStore.uiTheme}
                    footer={footer}
                  />
                  {editorStore.editorContentRendered ? <Invisible ref={onEditorRendered} /> : null}
                </>
              )}
              {editorStore.hasContentError && (
                <OutdatedClientNoteNotice
                  noteId={noteObservable.id}
                  isDesktop={clientEnvModule.isDesktop()}
                />
              )}
              {editorStore.hasFatalError && (
                <FatalEditorErrorNotice
                  noteId={noteObservable.id}
                  isDesktop={clientEnvModule.isDesktop()}
                />
              )}
            </>
          )}
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.mentionClientRect}
            placement="bottom-start"
            contentListClassName={insertMenuContentClassName}
            contentList={editorStore.mentionContentList}
          />
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.tableMenuClientRect}
            placement={editorStore.tableMenuPlacement}
            offsetOptions={editorStore.tableMenuOffsetOptions}
            contentList={editorStore.tableMenuContentList}
          />
          <MDSFloatingActionMenuDropdown
            zIndex={ZIndex.MentionsMenu}
            clientRect={editorStore.pasteMenuClientRect}
            placement="bottom-start"
            contentList={editorStore.pasteMenuContentListWithSelection}
          />
          <MentionTooltip
            mentionTooltipParams={editorStore.mentionTooltipParams}
            resetMentionTooltipParams={editorStore.resetMentionTooltipParams}
          />
          {/* Overlay spinner for version transitions */}
          <EditorSpinner
            isOverlay
            hideDelay={300}
            show={
              !!notesViewPage?.noteVersionHistory?.isLoading &&
              notesViewPage?.noteVersionHistory?.currentNoteId === editorStore.noteObservable?.id
            }
          />
        </EditorContainerStyles>
        {mode === NoteEditorMode.Editable && (
          <NoteActions
            editorStore={editorStore}
            shouldShowQuickActions={shouldShowQuickActions}
            versionHistoryActive={
              notesViewPage?.noteVersionHistory?.currentNoteId == noteObservable.id
            }
          />
        )}
        {shouldShowBottomBar && noteObservable instanceof NoteObservable && (
          <NoteEditorBottomBar
            note={noteObservable}
            tooltipLabel={editorStore.metadataTooltip}
            containerWidth={containerWidth}
          />
        )}
      </FormattingToolbarContext.Provider>
    );
  }
);

const NoteActions = observer(
  ({
    editorStore,
    shouldShowQuickActions,
  }: {
    editorStore: MemCommonEditorStore;
    versionHistoryActive: boolean;
    shouldShowQuickActions: boolean;
  }) => {
    if (shouldShowQuickActions) {
      return <EmptyNoteActions editorStore={editorStore} />;
    }
    return null;
  }
);

const TopPadding = styled.div`
  padding-top: ${({ theme }) => theme.spacing["5"]};
`;

// Free up store when component unmounts
const useUnmountEditorStore = (
  editorRef: MutableRefObject<MemCommonEditorStore | undefined>,
  noteObservable: INoteObservable | TemplateObservable,
  setEditorStore: (store: MemCommonEditorStore | undefined) => void
) => {
  useEffectOnMount(() => {
    return () => {
      if (editorRef?.current && noteObservable) {
        editorRef.current.unmount();
        setEditorStore(undefined);
      }
    };
  });
};

// Reset editor store to allow TitTap to re-initialize
const useNote = (
  editorStore: MemCommonEditorStore | undefined,
  noteObservable: INoteObservable | TemplateObservable,
  setEditorStore: (store: MemCommonEditorStore | undefined) => void,
  publicStore: PublicAppStore
) => {
  useEffect(() => {
    if (!editorStore) return;
    if (editorStore.id === noteObservable.id) return;

    if (noteObservable) {
      editorStore.unmount();
    }

    setEditorStore(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteObservable.id, publicStore]);
};

// Send highlight actions to the editor
interface UseHighlightingArg {
  highlightText: string | undefined;
  editorInstanceRef: MutableRefObject<MemCommonEditorInstance | null>;
  mode: NoteEditorMode;
}
const useHighlighting = ({ highlightText, editorInstanceRef, mode }: UseHighlightingArg) => {
  // Send a highlight command to the editor when its available, based on the highlight prop
  useEffect(() => {
    const animate = mode === NoteEditorMode.Editable;

    // when the user moves from one hover to another without the floating note preview being closed,
    // we need to clear out the previous highlight to avoid some jank
    if (editorInstanceRef.current) {
      editorInstanceRef.current?.dispatchAction?.({
        kind: MemCommonEditorActionKind.HighlightText,
        payload: {
          text: "",
          animate: false,
        },
      });
    }

    // Wait for the editor to be initialized and then highlight the text.
    // NOTE: Even if the instance was available in the above block, we should still use
    // this interval in case the editor is being re-initialized.
    let editorWatcherInterval: ReturnType<typeof setInterval> | null = setInterval(() => {
      if (editorInstanceRef.current?.dispatchAction) {
        if (highlightText) {
          editorInstanceRef.current.dispatchAction({
            kind: MemCommonEditorActionKind.HighlightText,
            payload: { text: highlightText, animate },
          });
        }

        // clear the interval
        if (editorWatcherInterval) {
          clearInterval(editorWatcherInterval);
          editorWatcherInterval = null;
        }
      }
    }, 50);

    return () => {
      // clear the interval
      if (editorWatcherInterval) {
        clearInterval(editorWatcherInterval);
        editorWatcherInterval = null;
      }
    };
  }, [editorInstanceRef, highlightText, mode]);
};

const Invisible = styled.span({ display: "none" });

const EditorContainerStyles = styled.div<{ $mode: NoteEditorMode }>`
  position: relative;

  ${({ $mode }) => $mode !== NoteEditorMode.Editable && `scrollbar-width: none;`}

  .tiptap.ProseMirror {
    --max-width: 700px;
  }
`;
